import React, { useState, useEffect, useRef } from 'react';
import { annuitySchemeList, annuityFrequencyList } from './data';
import CustomDropdown from './CustomDropdown';
import AnnuitySchemeDropdown from './AnnuitySchemeDropdown';
import { getBankPensionData, IMAGE_URL } from '../../../Api/commonAPI';
import { decryptData } from '../../../Decrypt/Decrypt';
import {
  validatePRCorpusLumpsum,
  validateNPSCorpus,
  validateWithdrawalPR,
  validateWithdrawalAmount,
  validateAnnuityValue,
  validateWorkSectorDOB,
  validateRequiredField,
  checkSectionCValidity,
  calculateAge
} from './ValidationUtils';

const SectionC = ({ formData: parentFormData, onDataChange, onValidationChange }) => {
  // Helper function to get current date in DD-MM-YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper function to format number with commas (e.g., 10000 -> 10,000)
  const formatNumberWithCommas = (value) => {
    if (!value || value === '') return '';
    // Remove all non-digit characters
    const numericValue = value.toString().replace(/,/g, '');
    if (numericValue === '') return '';
    // Add commas for thousands
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to remove commas and get numeric value
  const removeCommas = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  };

  // Helper function to convert ISO date format (YYYY-MM-DDTHH:mm:ss or YYYY-MM-DD) to DD-MM-YYYY
  const convertISOToDDMMYYYY = (dateString) => {
    if (!dateString) return '';

    try {
      // Handle ISO format: "2025-12-11T00:00:00" or "2025-12-11"
      let datePart = dateString;
      if (dateString.includes('T')) {
        datePart = dateString.split('T')[0];
      }

      // Check if already in DD-MM-YYYY format
      const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
      if (ddmmyyyyPattern.test(datePart)) {
        return datePart; // Already in correct format
      }

      // Convert YYYY-MM-DD to DD-MM-YYYY
      const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
      if (yyyymmddPattern.test(datePart)) {
        const parts = datePart.split('-');
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }

      return dateString; // Return as-is if format is unrecognized
    } catch (error) {
      console.error('Error converting date format:', error);
      return '';
    }
  };

  const defaultFormData = {
    normalWithdrawal: 'yes',
    PRCorpusLumpsum: '',
    annuityPercentage: '',
    WorkSectorName: '',
    WorkSectorDOB: getCurrentDate(),
    NPSCorpus: '',
    WithdrawalPR: '',
    WithdrawalAmount: '',
    AnnuityValue: '',
    AnnuitySchemeName: '',
    AnnuityFrequencySchemeName: ''
  };

  // Initialize with parentFormData if available, otherwise use default
  let initialFormData;
  if (parentFormData && parentFormData.PRCorpusLumpsum) {
    const processedParentData = { ...parentFormData };
    // Convert WorkSectorDOB from ISO format to DD-MM-YYYY if needed
    if (processedParentData.WorkSectorDOB) {
      processedParentData.WorkSectorDOB = convertISOToDDMMYYYY(processedParentData.WorkSectorDOB);
    }
    // Remove commas from NPSCorpus if present (store numeric value only)
    if (processedParentData.NPSCorpus) {
      processedParentData.NPSCorpus = removeCommas(processedParentData.NPSCorpus);
    }
    initialFormData = { ...defaultFormData, ...processedParentData };
  } else {
    initialFormData = defaultFormData;
  }

  const [formData, setFormData] = useState(initialFormData);

  // Refs to prevent circular updates
  const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
  const isUpdatingFromParentRef = useRef(false);
  const onDataChangeRef = useRef(onDataChange);

  // Update ref when onDataChange changes
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  const [currentAge, setCurrentAge] = useState(() => {
    // Calculate initial age from WorkSectorDOB using imported calculateAge
    if (initialFormData.WorkSectorDOB) {
      return calculateAge(initialFormData.WorkSectorDOB);
    }
    return calculateAge(getCurrentDate());
  });
  const [bankList, setBankList] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  // Validation errors state
  const [errors, setErrors] = useState({
    PRCorpusLumpsum: null,
    WorkSectorName: null,
    AnnuitySchemeName: null,
    AnnuityFrequencySchemeName: null,
    WorkSectorDOB: null,
    NPSCorpus: null,
    WithdrawalPR: null,
    WithdrawalAmount: null,
    AnnuityValue: null
  });

  const workSectorList = [
    'Central Goverment',
    'State Government',
    'Private Sector',
    'Public Sector'
  ];

  // Update local formData when parentFormData changes (from API response)
  useEffect(() => {
    if (!parentFormData) return;

    const parentDataStr = JSON.stringify(parentFormData);

    // Only update if parent data is different and we're not currently updating from parent
    if (parentDataStr !== lastParentDataRef.current && !isUpdatingFromParentRef.current) {
      // Check if parent has new data (like from API)
      const hasNewData = (parentFormData.PRCorpusLumpsum && parentFormData.PRCorpusLumpsum !== formData.PRCorpusLumpsum) ||
        (parentFormData.WorkSectorName && parentFormData.WorkSectorName !== formData.WorkSectorName) ||
        (parentFormData.NPSCorpus && parentFormData.NPSCorpus !== formData.NPSCorpus) ||
        (parentFormData.WorkSectorDOB && parentFormData.WorkSectorDOB !== formData.WorkSectorDOB);

      if (hasNewData) {
        isUpdatingFromParentRef.current = true;
        const processedParentData = { ...parentFormData };
        // Convert WorkSectorDOB from ISO format to DD-MM-YYYY if needed
        if (processedParentData.WorkSectorDOB) {
          processedParentData.WorkSectorDOB = convertISOToDDMMYYYY(processedParentData.WorkSectorDOB);
        }
        // Remove commas from NPSCorpus if present (store numeric value only)
        if (processedParentData.NPSCorpus) {
          processedParentData.NPSCorpus = removeCommas(processedParentData.NPSCorpus);
        }

        const updatedFormData = {
          ...formData,
          ...processedParentData
        };

        setFormData(updatedFormData);
        lastParentDataRef.current = parentDataStr;

        // Calculate age if WorkSectorDOB is provided
        if (updatedFormData.WorkSectorDOB) {
          const age = calculateAge(updatedFormData.WorkSectorDOB);
          setCurrentAge(age);
        }

        // Validate all fields after auto-fill
        const newErrors = {
          PRCorpusLumpsum: validatePRCorpusLumpsum(updatedFormData.PRCorpusLumpsum || ''),
          WorkSectorName: validateRequiredField(updatedFormData.WorkSectorName || '', 'Work Sector'),
          AnnuitySchemeName: validateRequiredField(updatedFormData.AnnuitySchemeName || '', 'Annuity Scheme'),
          AnnuityFrequencySchemeName: validateRequiredField(updatedFormData.AnnuityFrequencySchemeName || '', 'Annuity Frequency'),
          WorkSectorDOB: validateWorkSectorDOB(updatedFormData.WorkSectorDOB || ''),
          NPSCorpus: validateNPSCorpus(updatedFormData.NPSCorpus || ''),
          WithdrawalPR: validateWithdrawalPR(updatedFormData.WithdrawalPR || ''),
          WithdrawalAmount: validateWithdrawalAmount(updatedFormData.WithdrawalAmount || ''),
          AnnuityValue: validateAnnuityValue(updatedFormData.AnnuityValue || '')
        };
        setErrors(newErrors);

        setTimeout(() => {
          isUpdatingFromParentRef.current = false;
        }, 100);
      }
    }
  }, [parentFormData]);

  // Initial validation on mount if formData has values
  useEffect(() => {
    // Calculate age from WorkSectorDOB on mount
    if (formData.WorkSectorDOB) {
      const age = calculateAge(formData.WorkSectorDOB);
      setCurrentAge(age);
    }

    if (formData.PRCorpusLumpsum || formData.WorkSectorName || formData.NPSCorpus) {
      const initialErrors = {
        PRCorpusLumpsum: validatePRCorpusLumpsum(formData.PRCorpusLumpsum || ''),
        WorkSectorName: validateRequiredField(formData.WorkSectorName || '', 'Work Sector'),
        AnnuitySchemeName: validateRequiredField(formData.AnnuitySchemeName || '', 'Annuity Scheme'),
        AnnuityFrequencySchemeName: validateRequiredField(formData.AnnuityFrequencySchemeName || '', 'Annuity Frequency'),
        WorkSectorDOB: validateWorkSectorDOB(formData.WorkSectorDOB || ''),
        NPSCorpus: validateNPSCorpus(formData.NPSCorpus || ''),
        WithdrawalPR: validateWithdrawalPR(formData.WithdrawalPR || ''),
        WithdrawalAmount: validateWithdrawalAmount(formData.WithdrawalAmount || ''),
        AnnuityValue: validateAnnuityValue(formData.AnnuityValue || '')
      };
      setErrors(prev => ({ ...prev, ...initialErrors }));
    }
  }, []); // Run only on mount

  // Pass data to parent component whenever it changes (but not when updating from parent)
  useEffect(() => {
    if (onDataChangeRef.current && !isUpdatingFromParentRef.current) {
      onDataChangeRef.current(formData);
    }
  }, [formData]); // Removed onDataChange from dependencies, using ref instead

  useEffect(() => {
    // Calculate withdrawal amount and annuity value when NPSCorpus and WithdrawalPR change
    if (formData.NPSCorpus && formData.WithdrawalPR) {
      const corpus = parseFloat(formData.NPSCorpus) || 0;
      const withdrawalPercent = parseFloat(formData.WithdrawalPR) || 0;
      const withdrawalAmount = (corpus * withdrawalPercent) / 100;
      const annuityValue = corpus - withdrawalAmount;

      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: withdrawalAmount.toFixed(2),
        AnnuityValue: annuityValue.toFixed(2)
      }));

      // Validate calculated fields
      setErrors(prev => ({
        ...prev,
        WithdrawalAmount: validateWithdrawalAmount(withdrawalAmount.toFixed(2)),
        AnnuityValue: validateAnnuityValue(annuityValue.toFixed(2))
      }));
    } else if (formData.NPSCorpus && !formData.WithdrawalPR) {
      // If WithdrawalPR is empty, clear calculated fields and validate
      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: '',
        AnnuityValue: ''
      }));

      setErrors(prev => ({
        ...prev,
        WithdrawalAmount: validateWithdrawalAmount(''),
        AnnuityValue: validateAnnuityValue('')
      }));
    } else if (!formData.NPSCorpus || !formData.WithdrawalPR) {
      // If either field is empty, clear calculated fields
      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: '',
        AnnuityValue: ''
      }));

      setErrors(prev => ({
        ...prev,
        WithdrawalAmount: null,
        AnnuityValue: null
      }));
    }
  }, [formData.NPSCorpus, formData.WithdrawalPR]);

  // Fetch bank pension data when required fields change
  useEffect(() => {
    const fetchBankData = async () => {
      // Only fetch if all required fields are filled
      if (!currentAge || !formData.AnnuityValue || !formData.AnnuityFrequencySchemeName || !formData.AnnuitySchemeName) {
        setBankList([]);
        // Clear selected banks when bank list is cleared
        setSelectedBanks([]);
        // Notify parent that no bank is selected
        if (onDataChange) {
          onDataChange({
            selectedBank: null,
            annuityScheme: formData.AnnuitySchemeName,
            annuityFrequency: formData.AnnuityFrequencySchemeName
          });
        }
        return;
      }

      setLoadingBanks(true);
      try {
        const payload = {
          Age: currentAge,
          Amount: parseFloat(formData.AnnuityValue) || 0,
          AnnuityFrequency: formData.AnnuityFrequencySchemeName,
          AnnuityScheme: formData.AnnuitySchemeName,
          AnnuityValue: formData.AnnuityValue
        };

        const response = await getBankPensionData(payload);

        // Decrypt the response data
        if (response?.data?.data) {
          try {
            const decryptedData = decryptData(response.data.data);

            if (Array.isArray(decryptedData)) {
              setBankList(decryptedData);
            } else if (decryptedData && typeof decryptedData === 'object') {
              if (Array.isArray(decryptedData.data)) {
                setBankList(decryptedData.data);
              } else if (Array.isArray(decryptedData.BankList)) {
                setBankList(decryptedData.BankList);
              } else {
                setBankList([]);
              }
            } else {
              setBankList([]);
            }
          } catch (decryptError) {
            console.error('Error decrypting bank data:', decryptError);
            setBankList([]);
          }
        } else {
          setBankList([]);
        }
      } catch (error) {
        setBankList([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchBankData();
  }, [currentAge, formData.AnnuityValue, formData.AnnuityFrequencySchemeName, formData.AnnuitySchemeName]);

  // Pass validation status to parent (including bank selection check)
  useEffect(() => {
    if (onValidationChange) {
      const isFormValid = checkSectionCValidity(formData, errors);
      // Also check if bank is selected
      const isBankSelected = selectedBanks.length > 0;
      // Section C is valid only if form is valid AND bank is selected
      onValidationChange(isFormValid && isBankSelected);
    }
  }, [formData, errors, selectedBanks, onValidationChange]);

  // Sync selectedBanks state with parent data and notify parent if no bank is selected
  useEffect(() => {
    // If selectedBanks is empty, ensure parent knows no bank is selected
    if (selectedBanks.length === 0 && onDataChangeRef.current) {
      // Check if we need to notify parent (only if bankList is loaded)
      if (bankList.length > 0) {
        onDataChangeRef.current({
          selectedBank: null,
          annuityScheme: formData.AnnuitySchemeName,
          annuityFrequency: formData.AnnuityFrequencySchemeName
        });
      }
    }
  }, [selectedBanks, bankList, formData.AnnuitySchemeName, formData.AnnuityFrequencySchemeName]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let finalValue = value;

    // Validate PRCorpusLumpsum - max 20, allow empty, allow intermediate typing
    // Also sync with WithdrawalPR field
    if (name === 'PRCorpusLumpsum') {
      if (value === '' || value === null) {
        finalValue = '';
        // When empty, set annuityPercentage to empty and sync WithdrawalPR
        setFormData(prev => ({
          ...prev,
          [name]: '',
          WithdrawalPR: '',
          annuityPercentage: ''
        }));
        // Validate empty value
        const error = validatePRCorpusLumpsum('');
        setErrors(prev => ({
          ...prev,
          [name]: error,
          WithdrawalPR: validateWithdrawalPR('')
        }));
        return;
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          // Prevent values greater than 20 from updating state
          if (numValue > 20) {
            // Show error but don't update state
            const error = validatePRCorpusLumpsum(value);
            setErrors(prev => ({
              ...prev,
              [name]: error
            }));
            return; // Don't update formData
          }
          
          // Allow values between 0 and 20 - update state for all valid values
          // This allows typing "2" when user wants to type "20"
          finalValue = value;

          // Calculate annuityPercentage = 100 - PRCorpusLumpsum (only if valid)
          // Also sync the same value to WithdrawalPR
          if (numValue >= 0 && numValue <= 20) {
            const annuityPercent = 100 - numValue;
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              WithdrawalPR: finalValue, // Sync with WithdrawalPR
              annuityPercentage: annuityPercent.toString()
            }));
            // Validate WithdrawalPR as well
            const withdrawalError = validateWithdrawalPR(finalValue);
            setErrors(prev => ({
              ...prev,
              [name]: validatePRCorpusLumpsum(finalValue),
              WithdrawalPR: withdrawalError
            }));
          } else {
            // Still update the field but don't calculate annuity
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              WithdrawalPR: finalValue, // Sync with WithdrawalPR
              annuityPercentage: ''
            }));
            // Validate both fields
            setErrors(prev => ({
              ...prev,
              [name]: validatePRCorpusLumpsum(finalValue),
              WithdrawalPR: validateWithdrawalPR(finalValue)
            }));
          }
          return;
        }
      }
    }

    // Validate WithdrawalPR - max 20, also sync with PRCorpusLumpsum
    if (name === 'WithdrawalPR') {
      if (value === '' || value === null) {
        finalValue = '';
        // When empty, also clear PRCorpusLumpsum
        setFormData(prev => ({
          ...prev,
          [name]: '',
          PRCorpusLumpsum: '',
          annuityPercentage: ''
        }));
        // Validate both fields
        setErrors(prev => ({
          ...prev,
          [name]: validateWithdrawalPR(''),
          PRCorpusLumpsum: validatePRCorpusLumpsum('')
        }));
        return;
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          // Prevent values greater than 20 from updating state
          if (numValue > 20) {
            // Show error but don't update state
            const error = validateWithdrawalPR(value);
            setErrors(prev => ({
              ...prev,
              [name]: error
            }));
            return; // Don't update formData
          }
          
          // Allow values between 0 and 20
          finalValue = value;
          
          // Sync the same value to PRCorpusLumpsum
          // Calculate annuityPercentage = 100 - PRCorpusLumpsum (only if valid)
          if (numValue >= 0 && numValue <= 20) {
            const annuityPercent = 100 - numValue;
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              PRCorpusLumpsum: finalValue, // Sync with PRCorpusLumpsum
              annuityPercentage: annuityPercent.toString()
            }));
            // Validate both fields
            setErrors(prev => ({
              ...prev,
              [name]: validateWithdrawalPR(finalValue),
              PRCorpusLumpsum: validatePRCorpusLumpsum(finalValue)
            }));
          } else {
            // Still update the field but don't calculate annuity
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              PRCorpusLumpsum: finalValue, // Sync with PRCorpusLumpsum
              annuityPercentage: ''
            }));
            // Validate both fields
            setErrors(prev => ({
              ...prev,
              [name]: validateWithdrawalPR(finalValue),
              PRCorpusLumpsum: validatePRCorpusLumpsum(finalValue)
            }));
          }
          return;
        }
      }
    }

    // Handle NPSCorpus - format with commas for display, store numeric value
    if (name === 'NPSCorpus') {
      if (value === '' || value === null) {
        finalValue = '';
      } else {
        // Remove all non-digit characters (except decimal point if needed)
        const numericValue = value.replace(/[^\d]/g, '');
        if (numericValue === '') {
          finalValue = '';
        } else {
          // Format with commas for display
          finalValue = formatNumberWithCommas(numericValue);
        }
      }
      // Store the numeric value (without commas) in state for calculations
      const numericValueForState = removeCommas(finalValue);
      setFormData(prev => ({
        ...prev,
        [name]: numericValueForState // Store without commas for calculations
      }));

      // Validate using numeric value (without commas)
      const error = validateNPSCorpus(numericValueForState);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
      return; // Return early to prevent default handling
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : finalValue
    }));

    // Validate based on field name
    let error = null;
    if (name !== 'PRCorpusLumpsum' && name !== 'NPSCorpus') {
      switch (name) {
        case 'WithdrawalPR':
          error = validateWithdrawalPR(finalValue);
          break;
        case 'WithdrawalAmount':
          error = validateWithdrawalAmount(value);
          break;
        case 'AnnuityValue':
          error = validateAnnuityValue(value);
          break;
        case 'WorkSectorDOB':
          error = validateWorkSectorDOB(value);
          break;
        default:
          break;
      }
    } else {
      // Validate PRCorpusLumpsum
      if (finalValue === '') {
        error = validatePRCorpusLumpsum('');
      } else {
        error = validatePRCorpusLumpsum(finalValue);
      }
    }

    // Update errors
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Calculate age from date of birth
    if (name === 'WorkSectorDOB' && value) {
      const age = calculateAge(value);
      setCurrentAge(age);
    }
  };

  const handleDateChange = (e) => {
    const dateValue = e.target.value; // Format: YYYY-MM-DD

    if (dateValue) {
      // Convert YYYY-MM-DD to DD-MM-YYYY
      const [year, month, day] = dateValue.split('-');
      const formattedDate = `${day}-${month}-${year}`;

      setFormData(prev => ({
        ...prev,
        WorkSectorDOB: formattedDate
      }));

      // Validate date
      const error = validateWorkSectorDOB(formattedDate);
      setErrors(prev => ({
        ...prev,
        WorkSectorDOB: error
      }));

      // Calculate age
      const age = calculateAge(formattedDate);
      setCurrentAge(age);
    } else {
      setFormData(prev => ({
        ...prev,
        WorkSectorDOB: ''
      }));
      setErrors(prev => ({
        ...prev,
        WorkSectorDOB: validateWorkSectorDOB('')
      }));
      setCurrentAge(0);
    }
  };

  const convertToDateInputFormat = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const parts = ddmmyyyy.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to YYYY-MM-DD
    }
    return '';
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: value
      };

      // Validate dropdown fields
      let error = null;
      switch (name) {
        case 'WorkSectorName':
          error = validateRequiredField(value, 'Work Sector');
          break;
        case 'AnnuitySchemeName':
          error = validateRequiredField(value, 'Annuity Scheme');
          break;
        case 'AnnuityFrequencySchemeName':
          error = validateRequiredField(value, 'Annuity Frequency');
          break;
        default:
          break;
      }

      // Update errors
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));

      // Pass data to parent component
      if (onDataChange) {
        // Get selected bank - use null if no bank is selected
        const selectedBank = selectedBanks.length > 0 
          ? bankList.find(bank => selectedBanks.includes(bank.BankMasterID)) || null
          : null;
        
        onDataChange({
          selectedBank: selectedBank,
          annuityScheme: updatedData.AnnuitySchemeName,
          annuityFrequency: updatedData.AnnuityFrequencySchemeName
        });
      }

      return updatedData;
    });
  };

  const handleBankCheckboxChange = (bankId) => {
    setSelectedBanks(prev => {
      const isCurrentlySelected = prev.includes(bankId);
      const newSelected = isCurrentlySelected
        ? prev.filter(id => id !== bankId) // Remove if already selected
        : [bankId]; // Only allow single selection

      // Pass selected bank data to parent
      if (onDataChange) {
        if (isCurrentlySelected) {
          // Bank is being unselected - pass null
          onDataChange({
            selectedBank: null,
            annuityScheme: formData.AnnuitySchemeName,
            annuityFrequency: formData.AnnuityFrequencySchemeName
          });
        } else {
          // Bank is being selected - pass bank data
          const selectedBank = bankList.find(bank => bank.BankMasterID === bankId);
          onDataChange({
            selectedBank: selectedBank || null,
            annuityScheme: formData.AnnuitySchemeName,
            annuityFrequency: formData.AnnuityFrequencySchemeName
          });
        }
      }

      return newSelected;
    });
  };

  return (
    <div className="section-container">
      <div className="card shadow-sm">
        <div className="card-body p-4">
          <form>
            <div className="row g-3">
              {/* Normal Withdrawal Question */}
              <div className="col-12">
                <label className="form-label d-block mb-3">
                  Would you like to have normal withdrawal (Lump sum & Annuity withdrawal)
                </label>
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="normalWithdrawal"
                      id="withdrawalYes"
                      value="yes"
                      checked={formData.normalWithdrawal === 'yes'}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="withdrawalYes">
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="normalWithdrawal"
                      id="withdrawalNo"
                      value="no"
                      checked={formData.normalWithdrawal === 'no'}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="withdrawalNo">
                      No
                    </label>
                  </div>
                </div>
              </div>

              {/* Lump Sum Withdrawal Percentage */}
              <div className="col-md-6">
                <label htmlFor="PRCorpusLumpsum" className="form-label">
                  Percent of corpus opted for lump sum withdrawal (Max 20%)
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.PRCorpusLumpsum ? 'is-invalid' : ''}`}
                  id="PRCorpusLumpsum"
                  name="PRCorpusLumpsum"
                  value={formData.PRCorpusLumpsum}
                  onChange={handleInputChange}
                  min="0"
                  max="20"
                  step="0.01"
                  placeholder="Enter percentage (max 20%)"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.PRCorpusLumpsum && (
                  <div className="invalid-feedback d-block">
                    {errors.PRCorpusLumpsum}
                  </div>
                )}
              </div>

              {/* Annuity Purchase Percentage */}
              <div className="col-md-6">
                <label htmlFor="annuityPercentage" className="form-label">
                  Percent of corpus opted for purchase of annuity (Min 80%)
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="annuityPercentage"
                  name="annuityPercentage"
                  value={formData.annuityPercentage}
                  onChange={handleInputChange}
                  min="0"
                  max="80"
                  placeholder="Enter percentage (Min 80%)"
                  readOnly
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              {/* Work Sector */}
              <div className="col-md-6">
                <label htmlFor="WorkSectorName" className="form-label">
                  Work Sector
                </label>
                <CustomDropdown
                  options={workSectorList}
                  value={formData.WorkSectorName}
                  onChange={handleSelectChange}
                  placeholder="Select work sector..."
                  name="WorkSectorName"
                />
                {errors.WorkSectorName && (
                  <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.WorkSectorName}
                  </div>
                )}
              </div>

              {/* Subscriber's Date of Birth */}
              <div className="col-md-6">
                <label htmlFor="WorkSectorDOB" className="form-label">
                  Subscriber's Date of Birth
                </label>
                <input
                  type="date"
                  className={`form-control ${errors.WorkSectorDOB ? 'is-invalid' : ''}`}
                  id="WorkSectorDOB"
                  name="WorkSectorDOB"
                  value={convertToDateInputFormat(formData.WorkSectorDOB)}
                  onChange={handleDateChange}
                  max={new Date().toISOString().split('T')[0]}
                  style={{ cursor: 'pointer', textTransform: 'uppercase' }}
                />
                {currentAge > 0 && (
                  <div className="mt-2" style={{ fontSize: '0.875rem', color: '#000000', fontWeight: '500' }}>
                    <i className="fas fa-info-circle me-2" style={{ color: '#0d6efd' }}></i>
                    Your current age : {currentAge} years
                  </div>
                )}
                {errors.WorkSectorDOB && (
                  <div className="invalid-feedback d-block">
                    {errors.WorkSectorDOB}
                  </div>
                )}
              </div>


              {/* NPS Corpus */}
              <div className="col-md-6">
                <label htmlFor="NPSCorpus" className="form-label">
                  NPS Corpus
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.NPSCorpus ? 'is-invalid' : ''}`}
                  id="NPSCorpus"
                  name="NPSCorpus"
                  value={formData.NPSCorpus ? formatNumberWithCommas(formData.NPSCorpus) : ''}
                  onChange={handleInputChange}
                  placeholder="Enter NPS corpus amount (e.g., 10,000)"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.NPSCorpus && (
                  <div className="invalid-feedback d-block">
                    {errors.NPSCorpus}
                  </div>
                )}
              </div>

              {/* Withdrawal Percentage */}
              <div className="col-md-6">
                <label htmlFor="WithdrawalPR" className="form-label">
                  Withdrawal Percentage <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.WithdrawalPR ? 'is-invalid' : ''}`}
                  id="WithdrawalPR"
                  name="WithdrawalPR"
                  value={formData.WithdrawalPR}
                  onChange={handleInputChange}
                  placeholder="Enter withdrawal percentage (max 20%)"
                  min="0"
                  max="20"
                  step="0.01"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.WithdrawalPR && (
                  <div className="invalid-feedback d-block">
                    {errors.WithdrawalPR}
                  </div>
                )}
              </div>


              {/* Withdrawal Amount */}
              <div className="col-md-6">
                <label htmlFor="WithdrawalAmount" className="form-label">
                  Withdrawal Amount
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.WithdrawalAmount ? 'is-invalid' : ''}`}
                  id="WithdrawalAmount"
                  name="WithdrawalAmount"
                  value={formData.WithdrawalAmount}
                  onChange={handleInputChange}
                  placeholder="Withdrawal amount"
                  readOnly
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.WithdrawalAmount && (
                  <div className="invalid-feedback d-block">
                    {errors.WithdrawalAmount}
                  </div>
                )}
              </div>


              {/* Annuity Value */}
              <div className="col-md-6">
                <label htmlFor="AnnuityValue" className="form-label">
                  Annuity Value
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.AnnuityValue ? 'is-invalid' : ''}`}
                  id="AnnuityValue"
                  name="AnnuityValue"
                  value={formData.AnnuityValue}
                  onChange={handleInputChange}
                  placeholder="Annuity value"
                  readOnly
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.AnnuityValue && (
                  <div className="invalid-feedback d-block">
                    {errors.AnnuityValue}
                  </div>
                )}
              </div>


              {/* Select Annuity Scheme */}
              <div className="col-md-6">
                <label htmlFor="AnnuitySchemeName" className="form-label">
                  Select Annuity Scheme
                </label>
                <AnnuitySchemeDropdown
                  options={annuitySchemeList}
                  value={formData.AnnuitySchemeName}
                  onChange={handleSelectChange}
                  placeholder="Select annuity scheme..."
                  name="AnnuitySchemeName"
                />
                {errors.AnnuitySchemeName && (
                  <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.AnnuitySchemeName}
                  </div>
                )}
              </div>

              {/* Select Annuity Frequency */}
              <div className="col-md-6">
                <label htmlFor="AnnuityFrequencySchemeName" className="form-label">
                  Select Annuity Frequency
                </label>
                <CustomDropdown
                  options={annuityFrequencyList}
                  value={formData.AnnuityFrequencySchemeName}
                  onChange={handleSelectChange}
                  placeholder="Select annuity frequency..."
                  name="AnnuityFrequencySchemeName"
                />
                {errors.AnnuityFrequencySchemeName && (
                  <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.AnnuityFrequencySchemeName}
                  </div>
                )}
              </div>

            </div>
          </form>
        </div>
      </div>

      {/* Bank Pension List Section */}
      <div className="card shadow-sm mt-5">
        <div className="card-body p-3">
          {loadingBanks ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading bank list...</p>
            </div>
          ) : Array.isArray(bankList) && bankList.length > 0 ? (
            <div className='mt-3'>
              <h6 className="mb-3" style={{ fontSize: '1rem', fontWeight: '600', color: '#333' }}>
              Select Annuity Service Providers
              </h6>
              <div className="row g-3">
                {bankList.map((bank, index) => {
                  const bankId = bank.BankMasterID || bank.BankID || index;
                  const bankName = bank.BankName || bank.Name || 'Unknown Bank';
                  const bankLogo = bank.BankLogo || bank.Logo || '';
                  const pensionData = bank.BankPensionData || bank.PensionData || [];
                  const amount = pensionData[0]?.Amount || pensionData?.Amount || '0.00';
                  const percentage = pensionData[0]?.PensionPer || pensionData?.PensionPer || pensionData[0]?.Percentage || '0.0';

                  return (
                    <div key={bankId} className="col-md-4 col-sm-6 col-12">
                      <div
                        className="border rounded p-3"
                        style={{
                          backgroundColor: selectedBanks.includes(bankId) ? '#f0f8ff' : '#fff',
                          height: '100%',
                          boxShadow: selectedBanks.includes(bankId) ? '0 2px 6px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
                          transition: 'all 0.3s ease',
                          border: selectedBanks.includes(bankId) ? '2px solid #0d6efd' : '1px solid #dee2e6'
                        }}
                      >
                        <label
                          htmlFor={`bank-${bankId}`}
                          style={{ cursor: 'pointer', marginBottom: 0, display: 'block' }}
                        >
                          <div className="d-flex align-items-center mb-2">
                            {bankLogo && (
                              <img
                                src={bankLogo.startsWith('http') ? bankLogo : `${IMAGE_URL}${bankLogo}`}
                                alt={bankName}
                                style={{ width: '40px', height: '40px', objectFit: 'contain', marginRight: '10px' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="flex-grow-1">
                              <div style={{ fontSize: '0.9rem', fontWeight: '500', color: '#333' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`bank-${bankId}`}
                                  checked={selectedBanks.includes(bankId)}
                                  onChange={() => handleBankCheckboxChange(bankId)}
                                  style={{ cursor: 'pointer', marginRight: '8px' }}
                                />
                                <span>{bankName}</span>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '600' }}>
                              ₹ {parseFloat(amount).toFixed(2)}
                            </div>
                            <div style={{ fontSize: '1.1rem', color: '#28a745', fontWeight: '700' }}>
                              {parseFloat(percentage).toFixed(2)}%
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted mb-0">
                {formData.AnnuityValue && formData.AnnuityFrequencySchemeName && formData.AnnuitySchemeName && currentAge
                  ? 'No banks available for the selected criteria'
                  : 'Please fill all required fields to see bank list'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionC;

